// Ortak dashboard iskeleti. DashboardLayout ve AdminDashboardLayout
// aralarindaki fark sadece (a) sidebar navigation elemanlari,
// (b) sidebar altindaki sirket adi ve (c) main-inner'a eklenen sayfa bazli
// modifier classlari. Bu dosya uculu da prop olarak alir; geri kalan tum
// ortak davranisi (bildirimler, dropdown'lar, scroll-to-top, sidebar
// toggle, logout, theme/lang butonlari) buradan calisir.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSignOutAlt, FaBell, FaBars, FaTimes, FaUserCircle } from "react-icons/fa";
import "../styles/Dashboard.css";
import "../styles/mobile.css";
import useStackedTables from "../hooks/useStackedTables";
import SidebarNav from "./SidebarNav";
import { useI18n } from "../providers/I18nProvider";
import { useTheme } from "../providers/ThemeProvider";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";

const sortByReadStatus = (a, b) => {
  if (a.is_read && !b.is_read) return 1;
  if (!a.is_read && b.is_read) return -1;
  return 0;
};

const BaseLayout = ({
  children,
  navSections,
  companyName,
  // pathname alan, ek className dondurur ("main-inner--..." gibi). Opsiyonel.
  getMainInnerModifier,
}) => {
  const { user: userData, loading: authLoading, error: authError, logout } = useAuth();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();

  const mainInnerRef = useRef(null);
  useStackedTables(mainInnerRef);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const accountButtonRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get("/notifications");
      setNotifications([...response.data].sort(sortByReadStatus));
    } catch (error) {
      console.error("Bildirimler çekilirken hata oluştu:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await apiClient.put(`/notifications/${id}/read`, {});
    } catch (error) {
      console.error("Bildirim okundu olarak işaretlenirken hata:", error);
    }
  };

  const handleNotificationRead = (notification) => {
    markAsRead(notification.id);
    setNotifications((prev) =>
      prev
        .map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        .sort(sortByReadStatus)
    );
  };

  const deleteNotification = async (id) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error("Bildirim silinirken hata:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Sekme arka plandayken boşuna istek atma; öne gelince hemen yenile.
    const interval = setInterval(() => {
      if (!document.hidden) fetchNotifications();
    }, 30000);
    const onVisible = () => {
      if (!document.hidden) fetchNotifications();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Esc: açık menü/dropdown'ları kapat
  useEffect(() => {
    if (!sidebarOpen && !notificationsOpen && !accountOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setSidebarOpen(false);
      setNotificationsOpen(false);
      setAccountOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen, notificationsOpen, accountOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target) &&
        accountButtonRef.current &&
        !accountButtonRef.current.contains(event.target)
      ) {
        setAccountOpen(false);
      }
    };

    if (notificationsOpen || accountOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsOpen, accountOpen]);

  useEffect(() => {
    setAccountOpen(false);
    setNotificationsOpen(false);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 160);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("sidebar-open", sidebarOpen);
    return () => document.body.classList.remove("sidebar-open");
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const toggleNotifications = () => setNotificationsOpen((v) => !v);
  const toggleAccount = () => {
    setNotificationsOpen(false);
    setAccountOpen((v) => !v);
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const mainInnerClassName = useMemo(() => {
    const modifier =
      typeof getMainInnerModifier === "function"
        ? getMainInnerModifier(location.pathname) || ""
        : "";
    return ["main-inner", modifier].filter(Boolean).join(" ");
  }, [getMainInnerModifier, location.pathname]);

  if (authError) return <div>{t("common.error")}: {authError}</div>;
  if (authLoading || !userData) return <div>{t("common.loading")}</div>;

  return (
    <div className="dashboard-layout">
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label={t("topbar.closeMenu")}
        />
      )}
      <div
        id="app-sidebar"
        className={`sidebar ${sidebarOpen ? "is-open" : ""}`}
        aria-label={t("topbar.openMenu")}
      >
        <div className="sidebar-header">
          <div className="sidebar-username smaller-font">
            {userData.name} {userData.surname}
          </div>
          <div className="sidebar-company italic">{companyName}</div>
        </div>
        <SidebarNav
          sections={navSections}
          currentPath={location.pathname}
          onNavigate={(path) => {
            navigate(path);
            setSidebarOpen(false);
          }}
        />
      </div>

      <div className="main-content">
        {showScrollTop && (
          <button
            type="button"
            className="scroll-top-overlay"
            onClick={scrollToTop}
            aria-label="Yukarı git"
          >
            ↑ Yukarı
          </button>
        )}
        <div className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="menu-button"
              onClick={() => setSidebarOpen((prev) => !prev)}
              aria-expanded={sidebarOpen}
              aria-controls="app-sidebar"
              aria-label={sidebarOpen ? t("topbar.closeMenu") : t("topbar.openMenu")}
              title={sidebarOpen ? t("topbar.closeMenu") : t("topbar.openMenu")}
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
          <div className="topbar-right">
            <button
              ref={buttonRef}
              className="notification-button"
              onClick={toggleNotifications}
              aria-expanded={notificationsOpen}
              aria-haspopup="true"
              aria-label={t("topbar.notifications")}
            >
              <FaBell />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>
            {notificationsOpen && (
              <div ref={dropdownRef} className="notification-dropdown">
                {notifications.length > 0 ? (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`notification-item ${notif.is_read ? "is-read" : ""}`}
                      onClick={
                        !notif.is_read ? () => handleNotificationRead(notif) : undefined
                      }
                      role={!notif.is_read ? "button" : undefined}
                      tabIndex={!notif.is_read ? 0 : undefined}
                      onKeyDown={
                        !notif.is_read
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleNotificationRead(notif);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="notification-content">
                        <p>{notif.message}</p>
                        <small>
                          {new Date(notif.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                        </small>
                      </div>
                      <div className="notification-delete-section">
                        <button
                          className="notification-delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notif.id);
                          }}
                          aria-label={t("topbar.deleteNotification")}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="notification-item no-notifications">
                    <p className="notification-content p">
                      {t("topbar.noNotifications")}
                    </p>
                  </div>
                )}
              </div>
            )}
            <button
              className="topbar-action-button"
              onClick={toggleTheme}
              aria-label={t("topbar.theme")}
              title={t("topbar.theme")}
            >
              {theme === "dark" ? t("topbar.dark") : t("topbar.light")}
            </button>
            <button
              ref={accountButtonRef}
              className="topbar-action-button"
              onClick={toggleAccount}
              aria-expanded={accountOpen}
              aria-haspopup="true"
              aria-label={t("topbar.account")}
              title={t("topbar.account")}
            >
              <FaUserCircle />
            </button>
            {accountOpen && (
              <div ref={accountDropdownRef} className="topbar-dropdown account-dropdown">
                <button
                  type="button"
                  className="account-dropdown-item"
                  onClick={() => {
                    setAccountOpen(false);
                    navigate("/password");
                  }}
                >
                  {t("account.password.title")}
                </button>
              </div>
            )}
            <button
              className="topbar-action-button"
              onClick={() => setLang(lang === "tr" ? "en" : "tr")}
              aria-label={t("topbar.language")}
              title={t("topbar.language")}
            >
              {lang === "tr" ? t("topbar.tr") : t("topbar.en")}
            </button>
            <button className="logout-button" onClick={handleLogout}>
              <FaSignOutAlt className="logout-icon" />
            </button>
          </div>
        </div>

        <div className={mainInnerClassName} ref={mainInnerRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default BaseLayout;
